import { Coordinates } from "./types";
import { distanceRequestBatcher } from "./distanceRequestBatcher";
import logger from "./utils/logger";

export interface DistanceMatrixResult {
  originIndex: number;
  destinationIndex: number;
  distance: {
    text: string;
    value: number; // in meters
  };
  duration: {
    text: string;
    value: number; // in seconds
  };
  status: string;
}

export interface DistanceMatrixResponse {
  results: DistanceMatrixResult[];
  status: string;
}

/**
 * Calculate distance and duration between multiple origins and destinations using Google Maps
 * Uses request batching to reduce API calls when multiple requests arrive close together
 */
export const calculateDistanceMatrix = async (
  origins: (string | Coordinates)[],
  destinations: (string | Coordinates)[],
  mode: "driving" | "walking" | "bicycling" | "transit" = "driving",
): Promise<DistanceMatrixResponse> => {
  try {
    // For single origin + multiple destinations, use the batcher
    // This allows multiple components to batch their requests together
    if (origins.length === 1 && typeof origins[0] !== "string") {
      const origin = origins[0];
      const destCoords = destinations.filter(
        (dest): dest is Coordinates => typeof dest !== "string",
      );

      // If all destinations are coordinates, use batcher
      if (destCoords.length === destinations.length && destCoords.length > 0) {
        try {
          logger.log(
            `[DISTANCE] Using batcher for ${destCoords.length} destinations from origin ${origin.lat},${origin.lng}`,
          );
          const distances = await distanceRequestBatcher.queueRequest(
            origin,
            destCoords,
            mode,
          );

          // Transform batched results to match expected format
          const results: DistanceMatrixResult[] = distances.map(
            (distance, index) => ({
              originIndex: 0,
              destinationIndex: index,
              distance: {
                text: `${(distance * 1000).toFixed(0)} m`,
                value: distance * 1000, // Convert to meters
              },
              duration: {
                text: "N/A",
                value: 0,
              },
              status: "OK",
            }),
          );

          return {
            results,
            status: "OK",
          };
        } catch (batcherError) {
          // Fall through to direct API call if batcher fails
          console.warn(
            "[DISTANCE] Batcher failed, using direct API call:",
            batcherError,
          );
        }
      }
    }

    // Direct API call for cases where batching isn't applicable
    logger.log(
      `[DISTANCE] Direct API call (not using batcher): ${origins.length} origins, ${destinations.length} destinations`,
    );
    const response = await fetch("/api/maps/distance-matrix", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        origins: origins.map((origin) =>
          typeof origin === "string" ? origin : `${origin.lat},${origin.lng}`,
        ),
        destinations: destinations.map((dest) =>
          typeof dest === "string" ? dest : `${dest.lat},${dest.lng}`,
        ),
        mode,
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to calculate distance matrix");
    }

    return await response.json();
  } catch (error) {
    console.error("Error calculating distance matrix:", error);
    return {
      results: [],
      status: "ERROR",
    };
  }
};

/**
 * Calculate distance between two points using Google Maps
 */
export const calculateGoogleMapsDistance = async (
  origin: string | Coordinates,
  destination: string | Coordinates,
  mode: "driving" | "walking" | "bicycling" | "transit" = "driving",
): Promise<number> => {
  try {
    const result = await calculateDistanceMatrix([origin], [destination], mode);

    if (result.results.length > 0 && result.results[0].status === "OK") {
      // Return distance in kilometers
      return result.results[0].distance.value / 1000;
    }

    // Fallback to straight-line distance if Google Maps fails
    if (typeof origin === "object" && typeof destination === "object") {
      return calculateStraightLineDistance(origin, destination);
    }

    return 0;
  } catch (error) {
    console.error("Error calculating Google Maps distance:", error);

    // Fallback to straight-line distance if Google Maps fails
    if (typeof origin === "object" && typeof destination === "object") {
      return calculateStraightLineDistance(origin, destination);
    }

    return 0;
  }
};

/**
 * Calculate straight-line distance between two coordinates (fallback)
 * Uses the Haversine formula to calculate distance
 */
export const calculateStraightLineDistance = (
  coord1: Coordinates,
  coord2: Coordinates,
): number => {
  const R = 6371; // Radius of the Earth in kilometers
  const dLat = deg2rad(coord2.lat - coord1.lat);
  const dLng = deg2rad(coord2.lng - coord1.lng);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(coord1.lat)) *
      Math.cos(deg2rad(coord2.lat)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c; // Distance in kilometers
  return distance;
};

function deg2rad(deg: number): number {
  return deg * (Math.PI / 180);
}

/**
 * Find the closest location from a list of locations to a given point
 */
export const findClosestGoogleMapsLocation = async (
  userLocation: Coordinates,
  locations: Array<{ name: string; coordinates: Coordinates }>,
  mode: "driving" | "walking" | "bicycling" | "transit" = "driving",
): Promise<{
  name: string;
  coordinates: Coordinates;
  distance: number;
} | null> => {
  try {
    const destinations = locations.map((loc) => loc.coordinates);
    const result = await calculateDistanceMatrix(
      [userLocation],
      destinations,
      mode,
    );

    if (result.results.length === 0) {
      return null;
    }

    // Find the closest location
    let closestIndex = 0;
    let minDistance = Infinity;

    result.results.forEach((res, index) => {
      if (res.status === "OK" && res.distance.value < minDistance) {
        minDistance = res.distance.value;
        closestIndex = index;
      }
    });

    return {
      ...locations[closestIndex],
      distance: minDistance / 1000, // Convert to kilometers
    };
  } catch (error) {
    console.error("Error finding closest location:", error);
    return null;
  }
};
